import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import type { NewsletterResultCode } from '~/app/api/newsletter';
import { type FooterColumn } from '~/content/navigation';
import { Container, Grid } from '~/components/ui';
import { cn } from '~/lib/cn';
import {
  DEFAULT_LANGUAGE,
  LANGUAGE_LABELS,
  languageFromPathname,
  languagesWithTranslation,
  pathForLanguage,
  stripLanguagePrefix,
  type Language,
} from '~/lib/language';
import {
  isLocalizedCanonicalPath,
  LOCALIZED_LOCALES,
  localizedLocaleFromPathname,
  localizedPath,
  normalizeCanonicalPath,
} from '~/lib/localized-routes';
import { useContent, useLocalizedPath } from '~/lib/useLanguage';

// Section header = 15px token; links = 17px semibold token. Hover dims the
// other links in the same column (group/col) while the hovered one stays
// black — matching the mega menu.
const titleClass = 'text-[15px] font-medium leading-[1.32] tracking-[0.27px] text-grey-700';
const linkClass =
  'whitespace-nowrap py-3 text-[17px] leading-[1.16] font-semibold tracking-[-0.034px] text-black transition-colors ease-nav duration-150 group-has-[a:hover]/col:text-grey-500 hover:!text-black';

function FooterNav({ column }: { column: FooterColumn }) {
  const localizedPathFor = useLocalizedPath();

  return (
    <nav aria-label={column.title} className="group/col flex flex-col">
      <p className={titleClass}>{column.title}</p>
      <div className="mt-3 -mb-3 flex flex-col">
        {column.links.map((link) =>
          link.external ? (
            <a
              key={link.label}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className={linkClass}>
              {link.label}
            </a>
          ) : (
            <Link key={link.label} to={localizedPathFor(link.href)} className={linkClass}>
              {link.label}
            </Link>
          ),
        )}
      </div>
    </nav>
  );
}

const localeLabels = {
  'en-bd': 'Bangladesh',
  'en-pk': 'Pakistan',
  'en-eg': 'Egypt',
} as const;

/**
 * Language switcher (English ⇄ العربية).
 *
 * Plain anchors rather than router `Link`s, and rendered on the server, so the
 * alternate language is crawlable and works without JS. `hrefLang` gives crawlers
 * the reciprocal signal. There is no cookie or localStorage: the URL carries the
 * language, which keeps a shared link unambiguous and the CDN cache correct — a
 * cookie-based switch would make one URL serve two different languages and poison
 * the shared edge cache.
 */
function LanguageSelector() {
  const pathname = useRouterState({ select: ({ location }) => location.pathname });
  const activeLanguage = languageFromPathname(pathname);
  // `stripLanguagePrefix` also drops a regional `en-*` segment, so `/en-eg/pricing`
  // resolves to `/pricing` and the switcher shows up there like it does on the
  // canonical URL.
  const canonicalPath = stripLanguagePrefix(pathname);
  // Switching *to* English from a regional page keeps the visitor on their
  // regional URL rather than dropping them onto the canonical one.
  const activeLocale = localizedLocaleFromPathname(pathname);
  const { ui } = useContent();

  // Only offer languages this page actually exists in. Listing a language whose
  // translation has not shipped yet would hand the visitor a 404, so on
  // not-yet-translated pages there is nothing to switch to and the whole switcher
  // is hidden rather than rendered as a single dead option.
  const available = languagesWithTranslation(canonicalPath);
  if (available.length < 2) return null;

  const hrefFor = (language: Language) =>
    language === DEFAULT_LANGUAGE && activeLocale
      ? localizedPath(canonicalPath, activeLocale)
      : pathForLanguage(canonicalPath, language);

  return (
    <nav aria-label={ui.footer.languageLabel} className="mt-10 border-t border-black-6 pt-8">
      <p className={titleClass}>{ui.footer.languageLabel}</p>
      <div className="mt-3 flex flex-wrap gap-x-7 gap-y-1">
        {available.map((language) => {
          const active = language === activeLanguage;
          return (
            <a
              key={language}
              href={hrefFor(language)}
              hrefLang={language}
              lang={language}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'py-2 text-[16px] leading-[1.3] transition-colors ease-nav duration-150 hover:text-black',
                active ? 'font-semibold text-black' : 'text-grey-700',
              )}>
              {LANGUAGE_LABELS[language]}
            </a>
          );
        })}
      </div>
    </nav>
  );
}

/**
 * Server-rendered locale links for every route that existed in Webflow
 * Localization. These are ordinary anchors so crawlers and no-JS visitors can
 * discover each reciprocal regional URL directly from the page body.
 */
function LocaleSelector() {
  const { ui } = useContent();
  const pathname = useRouterState({ select: ({ location }) => location.pathname });
  const activeLocale = localizedLocaleFromPathname(pathname);
  // Strip the language prefix as well as the regional one, so an Arabic page
  // resolves to the same canonical path as its English twin and still lists the
  // regional URLs. They are all one hreflang cluster; this is the in-body version
  // of the alternates `pageHeadMeta` emits, for crawlers and no-JS visitors.
  const canonicalPath = normalizeCanonicalPath(
    stripLanguagePrefix(activeLocale ? pathname.slice(activeLocale.length + 1) || '/' : pathname),
  );

  if (!isLocalizedCanonicalPath(canonicalPath)) return null;

  // On an Arabic URL none of these is the current page — the language switcher
  // below owns that state.
  const onDefaultLanguage = languageFromPathname(pathname) === DEFAULT_LANGUAGE;
  const localeLinks = [
    {
      label: 'English',
      href: canonicalPath,
      hrefLang: 'en',
      active: onDefaultLanguage && !activeLocale,
    },
    ...LOCALIZED_LOCALES.map((locale) => ({
      label: localeLabels[locale],
      href: localizedPath(canonicalPath, locale),
      hrefLang: locale,
      active: onDefaultLanguage && activeLocale === locale,
    })),
  ];

  return (
    <nav
      aria-label={ui.footer.regionalVersionsLabel}
      className="mt-16 border-t border-black-6 pt-8">
      <p className={titleClass}>{ui.footer.regionalVersionsLabel}</p>
      <div className="mt-3 flex flex-wrap gap-x-7 gap-y-1">
        {localeLinks.map((link) => (
          <a
            key={link.hrefLang}
            href={link.href}
            hrefLang={link.hrefLang}
            aria-current={link.active ? 'page' : undefined}
            className={cn(
              'py-2 text-[16px] leading-[1.3] transition-colors ease-nav duration-150 hover:text-black',
              link.active ? 'font-semibold text-black' : 'text-grey-700',
            )}>
            {link.label}
          </a>
        ))}
      </div>
    </nav>
  );
}

export function Footer({
  className,
  showLegalDisclosure = true,
  legalNote,
}: { className?: string; showLegalDisclosure?: boolean; legalNote?: ReactNode } = {}) {
  const { navigation, ui } = useContent();
  const newsletterStatus = (code: NewsletterResultCode | undefined) => {
    switch (code) {
      case 'success':
        return ui.newsletter.success;
      case 'already_subscribed':
        return ui.newsletter.alreadySubscribed;
      case 'invalid_email':
        return ui.newsletter.invalidEmail;
      case 'unavailable':
        return ui.newsletter.unavailable;
      default:
        return ui.newsletter.error;
    }
  };
  const [email, setEmail] = useState('');
  const [subscription, setSubscription] = useState<{
    state: 'idle' | 'submitting' | 'success' | 'error';
    message?: string;
  }>({ state: 'idle' });

  const onSubscribe = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubscription({ state: 'submitting' });

    const form = new FormData(event.currentTarget);

    try {
      const response = await fetch('/api/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          company: form.get('company'),
        }),
      });
      const result = (await response.json().catch(() => null)) as {
        ok?: boolean;
        code?: NewsletterResultCode;
      } | null;

      // The API answers in English whatever the page language, so the status is
      // rendered from its result code rather than from its message.
      if (!response.ok || !result?.ok) {
        setSubscription({ state: 'error', message: newsletterStatus(result?.code) });
        return;
      }

      setEmail('');
      setSubscription({ state: 'success', message: newsletterStatus(result.code) });
    } catch {
      setSubscription({ state: 'error', message: ui.newsletter.error });
    }
  };

  const [products, tools, company, legal, calculators] = navigation.footerColumns;

  return (
    // Webflow `.footer-top`: z-index 3 so the homepage card hand (z-1 +
    // translateY 20rem) never paints over Subscribe / nav columns.
    <footer className={cn('relative z-[3] bg-white', className)}>
      <Container className="pt-20 md:pt-32">
        <Grid className="gap-y-14">
          {/* md: email [1–2] | empty [3] | Products [4–5] | Tools [6–7] | Calculators [8–9] */}
          <div className="col-span-9 h-fit md:col-span-2 md:col-start-1">
            <form
              onSubmit={onSubscribe}
              className="flex items-center gap-3 border-b border-black-6 pb-3 transition-colors focus-within:border-grey-900">
              <input
                type="email"
                name="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  if (subscription.state !== 'idle') setSubscription({ state: 'idle' });
                }}
                required
                autoComplete="email"
                placeholder={ui.newsletter.emailPlaceholder}
                aria-label={ui.newsletter.emailLabel}
                aria-describedby="newsletter-status"
                disabled={subscription.state === 'submitting'}
                className="w-full bg-transparent text-body-m text-ink outline-none placeholder:text-grey-400 disabled:opacity-60"
              />
              <input
                type="text"
                name="company"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="hidden"
              />
              <button
                type="submit"
                disabled={subscription.state === 'submitting'}
                aria-busy={subscription.state === 'submitting'}
                className="shrink-0 text-body-m font-medium text-red-500 transition-colors hover:text-red-700 disabled:cursor-wait disabled:text-grey-400">
                {subscription.state === 'submitting'
                  ? ui.newsletter.subscribing
                  : ui.newsletter.subscribe}
              </button>
            </form>
            <p
              id="newsletter-status"
              role="status"
              aria-live="polite"
              className={cn(
                'mt-2 min-h-[1.25rem] text-body-s',
                subscription.state === 'error' ? 'text-red-700' : 'text-grey-700',
              )}>
              {subscription.message}
            </p>
          </div>

          {/* Explicit empty track so column 3 cannot be auto-filled */}
          <div
            aria-hidden="true"
            className="pointer-events-none col-span-9 hidden md:col-span-1 md:col-start-3 md:block"
          />

          <div className="col-span-9 grid grid-cols-2 gap-x-5 gap-y-18 md:col-span-2 md:col-start-4 md:grid-cols-1">
            <FooterNav column={products} />
            <FooterNav column={company} />
          </div>

          <div className="col-span-9 grid grid-cols-2 gap-x-5 gap-y-18 md:col-span-2 md:col-start-6 md:grid-cols-1">
            <FooterNav column={tools} />
            <FooterNav column={legal} />
          </div>

          <div className="col-span-9 grid grid-cols-2 gap-x-5 gap-y-18 md:col-span-2 md:col-start-8 md:grid-cols-1">
            <FooterNav column={calculators} />
          </div>
        </Grid>
        <LocaleSelector />
        <LanguageSelector />
      </Container>

      {showLegalDisclosure ? (
        <div className="mt-24 bg-grey-25">
          <Container className="space-y-4 py-12 text-body-s text-grey-700">
            {legalNote && <div className="!mb-8 border-b border-black-6 pb-8">{legalNote}</div>}
            {ui.legal.paragraphs.map((paragraph, i) => (
              <p key={i}>
                {i === 0 ? (
                  <>
                    {ui.legal.copyright(new Date().getFullYear())}
                    <br />
                  </>
                ) : null}
                {paragraph}
              </p>
            ))}
          </Container>
        </div>
      ) : null}
    </footer>
  );
}
